import React, { useEffect, useState } from "react";
import uniqWith from "lodash/uniqWith";
import { Route, Switch, useRouteMatch } from "react-router-dom";
import { FxGuard } from "@coralblack/flax";
import { usePreScreeningSiteContext } from "@layouts/home/PreScreeningSiteContext";
import { PreScreeningEntrance } from "@pages/home/prescreening/PreScreeningEntrance";
import { PreScreeningQuestionnaires } from "@pages/home/prescreening/PreScreeningQuestionnaires";
import { PreScreeningDone, PreScreeningFailed } from "@pages/home/prescreening/PreScreeningResult";
import { InitialCrfPageSequence, InitialItemSequence, InitialVisitSequence, PreGroupId } from "maven-lib/dist/consts/edc";
import { CrfItemIdSpec, EcoaQuestionnairePageIdSpec, PreScreeningNoticeSpec } from "maven-lib/dist/consts/edc/crf";
import { StudyCaptureData } from "maven-lib/dist/dto/entity/studyCapture";
import { ecoaCrfApi } from "maven-lib/dist/states/apis/ecoaCrf";
import { useRoute } from "@maven-surface/hooks/useRoute";

export type PreScreeningCaptureItemSpec = Omit<CrfItemIdSpec, "groupId"> & { dbVal: string | number | null };

interface PreScreeningProps {
  studyId: bigint;
  preScreening: {
    questionnaire?: EcoaQuestionnairePageIdSpec;
    notice?: Array<PreScreeningNoticeSpec>;
    result?: {
      pass: Array<PreScreeningNoticeSpec>;
      fail: Array<PreScreeningNoticeSpec>;
    };
  };
  getPreProcessData: () => {
    encryptedPayload: string;
    captures: Array<StudyCaptureData>;
  };
}

export function PreScreening({ studyId, preScreening, getPreProcessData }: PreScreeningProps) {
  const match = useRouteMatch();

  const { selectedSite } = usePreScreeningSiteContext();

  const { pushPath } = useRoute();
  const [screeningCaptureData, setScreeningCaptureData] = useState<Array<PreScreeningCaptureItemSpec>>([]);

  const handleSaveCaptureData = (data: Array<PreScreeningCaptureItemSpec>) => {
    setScreeningCaptureData((prev) => uniqWith([...data, ...prev], (a, b) => a.itemId === b.itemId));
  };

  const getPreScreeningParams = (): { encryptedPayload: string; captures: Array<StudyCaptureData> } => {
    const { encryptedPayload, captures } = getPreProcessData();
    return {
      encryptedPayload,
      captures: [
        ...captures,
        ...screeningCaptureData.map((item: PreScreeningCaptureItemSpec) => ({
          id: {
            groupId: PreGroupId,
            folderId: item.folderId,
            visitId: item.visitId,
            visitSeq: item.visitSeq || InitialVisitSequence,
            crfPageId: item.crfPageId,
            crfPageSeq: item.crfPageSeq || InitialCrfPageSequence,
            sectionId: item.sectionId,
            itemId: item.itemId || "",
            itemSeq: InitialItemSequence,
          },
          val: {
            dbVal: item.dbVal,
            calcVal: null,
            removed: false,
            silent: false,
          },
        })),
      ],
    };
  };

  useEffect(() => {
    if (!getPreProcessData().encryptedPayload) pushPath(`/${studyId}/recruit`);
  }, [getPreProcessData, pushPath, selectedSite?.id, studyId]);

  return (
    <FxGuard
      api={{
        ...ecoaCrfApi.getEcoaPreScreening(studyId),
      }}
      render={(resp) => {
        if (resp) {
          return (
            <Switch>
              <Route path={`${match.url}/done`} component={() => <PreScreeningDone studyId={studyId} notice={preScreening.result!} />} />
              <Route
                path={`${match.url}/failed`}
                render={() => (
                  <PreScreeningFailed
                    studyId={studyId}
                    siteId={selectedSite?.id}
                    content={preScreening.result!.fail}
                    getPreScreeningParams={getPreScreeningParams}
                  />
                )}
              />
              <Route path={`${match.url}/:questionnaireSeq`}>
                <PreScreeningQuestionnaires
                  studyId={studyId}
                  siteId={selectedSite?.id}
                  screeningCaptureData={screeningCaptureData}
                  questionnaires={resp!.questionnaire.questionnaires}
                  onSaveCaptureData={handleSaveCaptureData}
                  getPreScreeningParams={getPreScreeningParams}
                />
              </Route>
              <Route exact path={`${match.url}`} component={() => <PreScreeningEntrance notice={preScreening.notice!} />} />
            </Switch>
          );
        }
      }}
    />
  );
}
